import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { useConsentActionsContext } from './use_consent_context.hook';
import { getAssignedEnvironmentSiteIds } from '../mom_adapter/assignment_join';
import { getAssignments } from '../mom_adapter/getters';
import { setAssignmentsForDocument } from '../mom_adapter/setters';
const uniqueSorted = (ids) => [...new Set(ids)].sort((left, right) => left - right);
const sameIdSet = (left, right) => {
    const a = uniqueSorted(left);
    const b = uniqueSorted(right);
    if (a.length !== b.length) {
        return false;
    }
    return a.every((id, index) => id === b[index]);
};
/**
 * Environment-scoped GET/PUT for one template lineage. Owns its environment picker so the Site
 * configuration tab's selection is not shared. PUT still replaces the full environment via
 * {@link setAssignmentsForDocument} so sibling templates stay assigned.
 */
export const useConsentTemplateSiteAssignments = ({ enabled, templateKey, entityId, studyId, sites, }) => {
    const { loadAssignmentCatalog } = useConsentActionsContext();
    const momClient = useApiClient(ApiClientService.MOM);
    const momClientRef = useRef(momClient);
    momClientRef.current = momClient;
    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(undefined);
    const [savedSiteIds, setSavedSiteIds] = useState([]);
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [loadState, setLoadState] = useState({ status: 'idle' });
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const environmentSites = useMemo(() => (sites ?? []).filter((site) => site.environmentId === selectedEnvironmentId), [sites, selectedEnvironmentId]);
    useEffect(() => {
        if (!enabled || !entityId || !studyId || selectedEnvironmentId === undefined) {
            setLoadState({ status: 'idle' });
            setSavedSiteIds([]);
            setSelectedSiteIds([]);
            return;
        }
        let cancelled = false;
        setLoadState({ status: 'loading' });
        setSaveError(null);
        (async () => {
            try {
                const catalog = await loadAssignmentCatalog();
                const next = await getAssignments(momClientRef.current, entityId, studyId, selectedEnvironmentId, catalog);
                if (cancelled) {
                    return;
                }
                const assigned = getAssignedEnvironmentSiteIds(next, templateKey);
                setSavedSiteIds(assigned);
                setSelectedSiteIds(assigned);
                setLoadState({ status: 'ready' });
            }
            catch (error) {
                if (cancelled) {
                    return;
                }
                setSavedSiteIds([]);
                setSelectedSiteIds([]);
                setLoadState({
                    status: 'error',
                    message: error instanceof Error ? error.message : 'Site assignments could not be loaded.',
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [enabled, entityId, studyId, selectedEnvironmentId, templateKey, loadAssignmentCatalog]);
    const setSiteAssigned = useCallback((environmentSiteId, assigned) => {
        setSelectedSiteIds((current) => {
            if (assigned) {
                return current.includes(environmentSiteId) ? current : [...current, environmentSiteId];
            }
            return current.filter((id) => id !== environmentSiteId);
        });
    }, []);
    const restoreSavedSelection = useCallback(() => {
        setSelectedSiteIds(savedSiteIds);
    }, [savedSiteIds]);
    const isDirty = !sameIdSet(selectedSiteIds, savedSiteIds);
    const save = useCallback(async () => {
        if (!entityId || !studyId || selectedEnvironmentId === undefined) {
            return 'Site assignments could not be saved.';
        }
        setIsSaving(true);
        setSaveError(null);
        const previous = savedSiteIds;
        try {
            const catalog = await loadAssignmentCatalog(true);
            if (!catalog.some((file) => file.templateKey === templateKey)) {
                throw new Error('Consent template is not in the assignment catalog');
            }
            const current = await getAssignments(momClientRef.current, entityId, studyId, selectedEnvironmentId, catalog);
            const next = await setAssignmentsForDocument(momClientRef.current, entityId, studyId, selectedEnvironmentId, current, templateKey, selectedSiteIds, environmentSites, catalog);
            const assigned = getAssignedEnvironmentSiteIds(next, templateKey);
            setSavedSiteIds(assigned);
            setSelectedSiteIds(assigned);
            return null;
        }
        catch (error) {
            setSelectedSiteIds(previous);
            const message = error instanceof Error ? error.message : 'Site assignments could not be saved.';
            setSaveError(message);
            return message;
        }
        finally {
            setIsSaving(false);
        }
    }, [
        loadAssignmentCatalog,
        entityId,
        environmentSites,
        savedSiteIds,
        selectedEnvironmentId,
        selectedSiteIds,
        studyId,
        templateKey,
    ]);
    return {
        selectedEnvironmentId,
        setSelectedEnvironmentId,
        environmentSites,
        selectedSiteIds,
        setSiteAssigned,
        restoreSavedSelection,
        loadState,
        isDirty,
        isSaving,
        saveError,
        save,
    };
};
